<template>
  <div v-if="singleSitePayload.length > 0">
    <div class="columns">
      <div class="column" :class="$style.headerTitleColumn">
        <label
          :class="[$style.tableName, $style.criteriaBasedLabel]"
          class="label"
          >CRITERIA BASED SAMPLING (OPTIONAL)</label
        >
      </div>
    </div>
    <b-table :class="$style.criteriaBasedTable" :data="auditCriteriaList">
      <template slot-scope="props">
        <b-table-column field="index" label="No.">
          {{ props.index + 1 }}.
        </b-table-column>
        <b-table-column
          field="criteria"
          label="Data Collection Period Criteria"
        >
          {{ props.row.criteriaTitle }}
        </b-table-column>
        <b-table-column width="140px" field="sampleSize" label="Sample Size">
          <template slot="header">
            Sample Size
            <ConsecutiveCalculator
              v-if="editable"
              :initialData="calculateData"
              @applySampleSize="handleApplySampleSize"
            ></ConsecutiveCalculator>
          </template>
          <b-input
            :disabled="!editable"
            :class="$style.numberInput"
            v-model="props.row.sampleSize"
            type="number"
            onkeydown="javascript: return event.keyCode === 8 || event.keyCode === 46 ? true : !isNaN(Number(event.key))"
            min="0"
            oninput="javascript: if (this.value.length > this.maxLength) this.value = this.value.slice(0, this.maxLength);"
            maxlength="7"
          ></b-input>
        </b-table-column>
      </template>
    </b-table>

    <div :class="$style.saveChangesContainer" v-if="editable">
      <b-button
        @click="handleSingleSiteChanges"
        :class="$style.saveChangesBtn"
        type="is-primary"
        >Save</b-button
      >
    </div>
  </div>
</template>
<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import DefaultLayout from '@/layouts/DefaultLayout.vue';
import { Action, State } from 'vuex-class';
import HeroBar from '@/components/base/HeroBar.vue';
import { isDifferent, isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import {
  AuditCriteriaMapStatus,
  AuditSamplingDataConfig,
  AuditSamplingResponse,
  SingleSitePayload,
  SingleSiteSamplingRequestPayload,
} from '@/store/modules/audits/types/audits.types';
import {
  map as _map,
  round as _round,
  cloneDeep as _cloneDeep,
  orderBy as _orderBy,
  findIndex as _findIndex,
} from 'lodash';
import { ToastProgrammatic as Toast } from 'buefy';
import SamplingCalculator from '@/views/Audit/components/SamplingCalculator.vue';
import ConsecutiveCalculator from '@/views/Audit/components/ConsecutiveCalculator.vue';
import {
  AuditSamplingType,
  ConsecutiveSamplingMode,
} from '@/jbi-shared/types/project.types';

@Component({
  components: {
    ConsecutiveCalculator,
    SamplingCalculator,
    DefaultLayout,
    HeroBar,
  },
})
export default class ConsecutiveTargetSampleSingleSiteSampling extends Vue {
  @Prop()
  public auditId!: number;
  @Prop()
  public auditConfig!: AuditSamplingResponse;
  @Prop()
  public auditSamplingType!: AuditSamplingType;
  @Prop()
  public consecutiveSamplingMode!: ConsecutiveSamplingMode;
  @Prop({ default: true })
  public editable!: boolean;
  public singleSitePayload: SingleSitePayload[] = [];
  public calculateData = {
    output: 0,
  };

  @Action('audits/updateSingleSiteSampling')
  public updateSingleSiteSampling!: (
    payload: SingleSiteSamplingRequestPayload,
  ) => void;

  @State((state) => state.audits.updatedSingleSiteSampling)
  public updatedSingleSiteSampling!: any;

  @Watch('updatedSingleSiteSampling')
  @isTruthy
  public watchUpdatedSingleSiteSampling() {
    this.showNotificationAndRedirectToProject();
  }

  public mounted() {
    this.populateSingeSiteData();
  }

  public populateSingeSiteData() {
    _map(this.auditConfig.dataConfigs, (dataConfig: any) => {
      const currentCriteriaPayload: SingleSitePayload = {
        auditId: this.auditId,
        criteriaId: 0, // todo: change this logic later
        auditCriteriaId: 0,
        sampleSize: 0,
      };
      _map(dataConfig, (singleConfig: AuditSamplingDataConfig) => {
        if (
          singleConfig.auditCriteriaMap.status === AuditCriteriaMapStatus.ACTIVE
        ) {
          currentCriteriaPayload.criteriaId =
            singleConfig.auditCriteriaMap.criteria.id;
          currentCriteriaPayload.auditId = this.auditId;
          currentCriteriaPayload.auditCriteriaId =
            singleConfig.auditCriteriaMap.id;
          currentCriteriaPayload.criteriaTitle =
            singleConfig.auditCriteriaMap.criteria.title;
          currentCriteriaPayload.order = singleConfig.auditCriteriaMap.order;
          if (singleConfig.auditSiteMap === null) {
            currentCriteriaPayload.sampleSize = _round(singleConfig.sampleSize);
            currentCriteriaPayload.dataConfig = singleConfig;
          }
        }
      });
      this.singleSitePayload.push(currentCriteriaPayload);
    });
  }

  public showNotificationAndRedirectToProject() {
    Toast.open({
      message: `Changes saved`,
      position: 'is-top',
      type: 'is-dark',
      duration: 3500,
    });
  }

  public handleSingleSiteChanges() {
    this.updateSingleSiteSampling({
      auditId: this.auditId,
      singleSitePayload: this.singleSitePayload,
      auditSamplingType: this.auditSamplingType,
      calculateData: this.calculateData,
      auditSamplingMode: this.consecutiveSamplingMode,
      samplingConfigId: this.auditConfig.auditSamplingConfigs.id,
    });
  }

  public handleApplySampleSize(calculateData: any) {
    if (calculateData.auditCriteriaId !== undefined) {
      const updateDataPayloadIndex = _findIndex(this.auditCriteriaList, {
        auditCriteriaId: calculateData.auditCriteriaId,
      });
      this.auditCriteriaList[updateDataPayloadIndex].sampleSize =
        calculateData.output;
    } else {
      this.calculateData = calculateData;
      this.auditCriteriaList.map((payload) => {
        payload.sampleSize = calculateData.output;
      });
    }
  }

  get auditCriteriaList() {
    return _orderBy(this.singleSitePayload, ['order'], ['asc']);
  }
}
</script>
<style lang="scss" module>
.tableName {
  color: #444f51;
  font-weight: bold;
}
.saveChangesContainer {
  padding-top: 60px;
  text-align: right;
}
.saveChangesBtn {
  float: right;
  background-color: #00205b;
  border: 0.5px solid #00205b;
  border-radius: 24px;
  box-shadow: 0 2px 4px 0 rgba(29, 112, 184, 0.33);
  height: 48px;
  padding: 0 calc(1em + 0.25em);
  color: #ffffff;
  font-size: 18px !important;
  font-weight: bold !important;
}
.headerTitleColumn {
  padding: 0 0.75rem;
}
.criteriaBasedLabel {
  margin-top: 12px;
  padding-top: 24px;
}
.numberInput {
  width: 80px !important;
  height: 44px;
  input {
    background-color: white !important;
    border-color: #dfdfdf !important;
    color: #444f51 !important;
    text-align: right;
    height: 44px;
    padding: 7px 7px;
  }
}
input[type='number'] {
  -moz-appearance: textfield;
}
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
}
.criteriaBasedTable > div > table {
  thead {
    background-color: #ffffff;
    tr {
      font-size: 14px;
      th {
        color: #9f9f9f;
        font-weight: initial !important;
        padding: 20px 0px 23px 12px;
      }
      th:first-child {
        padding-left: 20px;
      }
      th:last-child {
        padding-left: 0;
        padding-right: 20px;
      }
      th:nth-child(3) {
        div {
          float: right;
        }
      }
    }
  }
  tbody {
    background-color: #ffffff;
    tr {
      td {
        color: #444f51;
        border: 0.5px solid #dbdbdb;
        border-width: 0 0 0.5px;
        padding-top: 20px;
        padding-bottom: 18px;
        padding-right: 0px;
      }
      td:first-child {
        padding-left: 20px;
      }
      td:last-child {
        padding-left: 0;
        padding-right: 20px;
        padding-top: 20px;
      }
      td:nth-child(3) {
        width: 140px;
        float: right;
        display: flex;
        justify-content: flex-end;
      }
    }
    tr:last-child {
      td {
        padding-bottom: 20px;
      }
    }
  }
}
</style>
